import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
  EscalatedTicket,
  PartialTicket,
  TicketComment,
  TicketCommentCreationFields,
  TicketCommentEditFields,
  TicketCommentResult,
  TicketsService
} from '@app/shared/services';
import { LoggedInUser } from '@app/shared/services/api/logged-in-user';
import { UserProfileService } from '@app/shared/services/api/user-profile.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TicketCommentTrackingEvent, TicketTrackingService } from '@app/layout/sites/tickets/ticket-tracking.service';
import { User } from '@app/layout/admin/services/api/user-interfaces';
import { CommentMentionItem } from '../comment-mention/comment-mention.directive';

export enum UpsertTicketCommentState {PROCESSING, DONE, ERROR}

export interface UpsertTicketCommentResult {
  comment?: TicketComment;
  ticket?: PartialTicket;
}

export interface UpsertTicketCommentProgress {
  state: UpsertTicketCommentState;
  result?: UpsertTicketCommentResult;
  error?: Error;
}

@Component({
  selector: 'app-upsert-ticket-comment',
  templateUrl: './upsert-ticket-comment.component.html',
  styleUrls: ['./upsert-ticket-comment.component.scss'],
  standalone: false
})
export class UpsertTicketCommentComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() ticket: EscalatedTicket;
  @Input() ticketId: string;
  @Input() ticketSummary: string;
  @Input() siteId: number;
  @Input() text: string;
  @Input() comment: TicketComment;
  @Input() rows = 6;
  @Input() showLabels = true;
  @Input() grabFocus = false;
  @Input() showErrors = false;
  @Input() includeTicketUpdateFields = true;
  @Input() placeholder: string;
  @Input() requireComment = true;
  @Input() assigneesLoading: boolean;
  @Input() assigneesByEntityName: Map<string, User[]>;
  @Input() disableMentions = false;
  @Output() progressEvent: EventEmitter<UpsertTicketCommentProgress> = new EventEmitter<UpsertTicketCommentProgress>();
  @Output() commentFocus = new EventEmitter<boolean>();
  @ViewChild(NgForm, {static: true}) private form: NgForm;
  @ViewChild('commentBox') commentBoxElementRef: ElementRef<HTMLTextAreaElement>;

  isCreate = true;
  author: string;
  commentText: string;
  user: LoggedInUser;

  constructor(private ticketsService: TicketsService, private userProfileService: UserProfileService,
              private ticketTrackingService: TicketTrackingService) {}

  ngOnInit(): void {
    this.user = this.userProfileService.getLoggedInUser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.comment || changes.text) {
        this.isCreate = !this.comment;
        if (this.comment) {
          if (this.text != null) {
            throw new Error('Cannot supply both text and comment');
          }
          this.commentText = this.comment.text;
        } else {
          this.commentText = this.text;
        }
      }

      if (changes.ticketId || changes.ticket) {
        if (this.ticket != null) {
          if (this.ticketId == null) {
            this.ticketId = this.ticket.ticket_id;
          } else {
            throw new Error('Cannot supply both ticketId and ticket');
          }

          if (this.ticketSummary == null) {
            this.ticketSummary = this.ticket.summary;
          } else {
            throw new Error('Cannot supply both ticket and ticketSummary');
          }
          if (this.siteId == null) {
            this.siteId = this.ticket.site_id;
          } else {
            throw new Error('Cannot supply both ticket and site_id');
          }
        } else if (this.ticketId == null) {
          throw new Error('ticketId or ticket need to be provided');
        }
      }

      if (this.comment && this.comment.ticket_id !== this.ticketId) {
        throw new Error('The ticket_id of comment ' + this.comment.ticket_id + ' does not match ticketId' + this.ticketId);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.grabFocus && this.commentBoxElementRef) {
      this.focus();
    }
  }

  onSubmit(commentTransformer?: (comment: string) => string,
           submitCallback?: () => void): void {
    const comment = typeof commentTransformer === 'function' ? commentTransformer(this.commentText ?? '') : this.commentText;
    if (this.valid) {
      this.submitComment(comment, submitCallback);
    } else {
      throw new Error('Comment is empty');      
    }
  }

  private submitComment(comment: string, submitCallback?: () => void): void {
    this.progressEvent.next({state: UpsertTicketCommentState.PROCESSING});
    let upsertObservable: Observable<TicketCommentResult>;
    if (this.isCreate) {
      const ticketComment: TicketCommentCreationFields = {
        ticket_id: this.ticketId,
        text: comment,
        ...!this.user && {anonymous_author_name: this.author}
      };
      upsertObservable = this.ticketsService.createTicketComment(ticketComment, this.includeTicketUpdateFields).pipe(map(result => {
        const comment = result.ticket_comment;
        if (comment && !comment.author && this.user) {
          comment.author = this.user.asUser();
        }
        return result;
      }));
    } else {
      const ticketComment: TicketCommentEditFields = {
        comment_id: this.comment.comment_id,
        ticket_id: this.comment.ticket_id,
        text: comment
      };
      upsertObservable = this.ticketsService.updateTicketComment(ticketComment, this.includeTicketUpdateFields);
    }
    upsertObservable.subscribe({
      next: (result: TicketCommentResult) => {
        const comment = result.ticket_comment;
        const extraEventProperties = this.ticket ? null : {ticket_id: this.ticketId, ticket_name: this.ticketSummary, site_id: this.siteId};
        const event = this.isCreate ? (comment.author_id == null ? TicketCommentTrackingEvent.ANONYMOUS_CREATED : TicketCommentTrackingEvent.CREATED)
          : TicketCommentTrackingEvent.EDITED;
        this.ticketTrackingService.trackComment(event, this.ticket, comment, extraEventProperties);
        const progressPayload: UpsertTicketCommentProgress = {state: UpsertTicketCommentState.DONE, result: {comment}};
        const ticket = result.ticket;
        if (this.includeTicketUpdateFields && ticket) {
          progressPayload.result.ticket = ticket;
        }
        this.commentText = '';
        this.progressEvent.next(progressPayload);
      },
      error: (error) => this.progressEvent.next({state: UpsertTicketCommentState.ERROR, error}),
      complete: () => submitCallback?.()
    });
  }

  get valid(): boolean {
    return this.form.valid;
  }

  emitFocusEvent(focused: boolean): void {
    this.commentFocus.next(focused);
  }

  hasText(): boolean {
    return this.commentText && !!this.commentText.trim().length;
  }

  getText(): string {
    return this.commentText;
  }

  focus(): void {
    setTimeout(() => this.commentBoxElementRef?.nativeElement?.focus());
  }

  addMention(mention: string): void {
    if (!this.commentText) {
      this.commentText = mention + ' ';
    } else {
      const cursorIndex = this.commentBoxElementRef.nativeElement.selectionStart;
      const startText = this.commentText.slice(0, cursorIndex);
      this.commentText = startText + (startText.charAt(startText.length - 1).trim() && ' ')
        + mention + ' ' + this.commentText.slice(cursorIndex);
    }
    this.commentBoxElementRef.nativeElement.focus();
  }

  /** cancels click event in case user clicks on group item */
  itemElementClickHandler(item: CommentMentionItem, event: MouseEvent): void | boolean {
    if (item && (item.isGroup || item.isSpinner)) {
      event.stopPropagation();
      return false;
    }
  }
}
